import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PromptModalOptions, selectIsMobile } from 'redux/mode/reducers';
import { showPromptModal } from 'redux/mode/actions';
import Modal from '..';
import TextInput from 'components/Widget/Input/TextInput';
import MultilineInput from 'components/Widget/Input/MultilineInput';
import { useScrollIntoView } from 'hooks/useScrollIntoView';
import { RootReducer } from 'redux/reducers';
import * as S from './styles';

interface PromptModalProps {
	options: PromptModalOptions;
}

const PromptModal = ({ options }: PromptModalProps) => {
	const {
		title,
		cancelLabel,
		submitLabel,
		description,
		placeholder,
		children,
		disabled,
		isMultilineInput,
		onCancel,
		onSubmit,
		initialValue,
		validate,
		filter,
	} = options;
	const [value, setValue] = useState('');
	const [error, setError] = useState<string | null>(null);
	const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
	const isMobile = useSelector((state: RootReducer) => selectIsMobile(state.mode));
	const disable =
		disabled?.(value) === undefined ? (!value || error ? true : false) : disabled(value);

	const dispatch = useDispatch();

	// scroll input into view when keyboard appears on mobile
	useScrollIntoView(inputRef, isMobile);

	useEffect(() => {
		const newValue = initialValue || '';
		setValue(newValue);
		validate && setError(validate(newValue));
	}, []);

	const onInput = (e: any) => {
		const input = filter ? filter(e.target.value) : e.target.value;
		setValue(input);
		validate && setError(validate(input));
	};

	const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			if (!disable && !error) submit();
		}
	};

	const resetAndClose = () => {
		setValue('');
		setError(null);
		dispatch(showPromptModal(null));
	};

	const cancel = () => {
		resetAndClose();
		onCancel?.();
	};

	const submit = () => {
		resetAndClose();
		onSubmit(value);
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	return (
		<Modal
			title={title}
			cancelLabel={cancelLabel || 'Cancel'}
			submitLabel={submitLabel}
			description={description}
			onCancel={cancel}
			onSubmit={submit}
			disabled={disable}
		>
			<S.Content>
				<S.MessageContainer>
					{isMultilineInput ? (
						<S.MultilineInput>
							<MultilineInput
								ref={inputRef as React.RefObject<HTMLTextAreaElement>}
								value={value}
								onInput={onInput}
								onKeyDown={onKeyDown}
								placeholder={placeholder}
								minLines={2}
								data-testid="modal-prompt-input"
							/>
						</S.MultilineInput>
					) : (
						<S.TextInput>
							<TextInput
								ref={inputRef as React.RefObject<HTMLInputElement>}
								type="text"
								value={value}
								onChange={onInput}
								onKeyDown={onKeyDown}
								placeholder={placeholder}
								spellCheck={false}
								data-testid="modal-prompt-input"
							/>
						</S.TextInput>
					)}
				{error?.trim() && <S.Error>{error}</S.Error>}
				</S.MessageContainer>
				{children}
			</S.Content>
		</Modal>
	);
};

export default PromptModal;
